import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { API, BACKEND_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { CountdownTimer } from '../components/CountdownTimer';
import BidModal from '../components/BidModal';

const MyBidsPage = () => {
  const { user } = useAuth();
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAuction, setModalAuction] = useState(null);
  const wsRefs = useRef({});

  useEffect(() => {
    if (user) fetchMyBids();
    return () => {
      Object.values(wsRefs.current).forEach(ws => {
        if (ws) { ws.onclose = null; ws.close(); }
      });
    };
  }, [user]);

  const fetchMyBids = async () => {
    try {
      const response = await axios.get(`${API}/my-bids`);
      setMyBids(response.data);
      response.data.forEach(bid => {
        if (bid.status === 'active' && !wsRefs.current[bid.auction_id]) {
          connectWs(bid.auction_id);
        }
      });
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectWs = (auctionId) => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const ws = new WebSocket(`${wsProtocol}//${wsHost}/api/ws/auction/${auctionId}`);
    wsRefs.current[auctionId] = ws;

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_bid') fetchMyBids();
    };

    ws.onclose = () => { delete wsRefs.current[auctionId]; };

    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      else clearInterval(ping);
    }, 30000);
  };

  const openBidModal = (bid) => {
    setModalAuction({
      auction_id: bid.auction_id,
      title: bid.title,
      current_price: bid.current_price,
      min_increment: bid.min_increment || 100,
      total_bids: bid.total_bids || 0,
      status: bid.status,
      end_time: bid.end_time,
      horse: {
        name: bid.horse_name,
        images: bid.horse_image ? [bid.horse_image] : []
      }
    });
  };

  if (!user) return <Navigate to="/login" />;

  const activeBids = myBids.filter(b => b.status === 'active');
  const endedBids = myBids.filter(b => b.status === 'ended');

  return (
    <div className="bg-white min-h-screen" data-testid="my-bids-page">
      <div className="border-b border-gray-200">
        <div className="container-custom px-4 py-6 md:py-8">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0a1628]" data-testid="my-bids-title">Le mie offerte</h1>
        </div>
      </div>

      <div className="container-custom px-4 py-6 md:py-10 max-w-3xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : myBids.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-[#64748b] text-lg">Non hai ancora fatto offerte</p>
            <a href="/aste" className="inline-block mt-4 bg-[#0a7e8c] text-white px-6 py-3 rounded font-medium hover:bg-[#086a76] transition-colors">
              Scopri le aste attive
            </a>
          </div>
        ) : (
          <div className="space-y-8">
            {activeBids.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide mb-4">Aste in corso ({activeBids.length})</h2>
                <div className="space-y-3">
                  {activeBids.map(bid => (
                    <BidCard key={bid.auction_id} bid={bid} onBid={openBidModal} />
                  ))}
                </div>
              </div>
            )}
            {endedBids.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#64748b] uppercase tracking-wide mb-4">Aste concluse ({endedBids.length})</h2>
                <div className="space-y-3">
                  {endedBids.map(bid => (
                    <BidCard key={bid.auction_id} bid={bid} ended />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {modalAuction && (
        <BidModal
          auction={modalAuction}
          onClose={() => setModalAuction(null)}
          onBidPlaced={() => { setModalAuction(null); fetchMyBids(); }}
        />
      )}
    </div>
  );
};

const BidCard = ({ bid, ended, onBid }) => {
  const isLeading = bid.is_leading;

  return (
    <div
      className={`rounded-lg border-2 transition-colors ${
        ended
          ? isLeading ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50/50'
          : isLeading ? 'border-green-400 bg-green-50' : 'border-red-400 bg-red-50'
      }`}
      data-testid={`my-bid-${bid.auction_id}`}
    >
      {/* Desktop layout */}
      <div className="hidden md:flex items-center gap-4 p-4">
        <a href={`/asta/${bid.auction_id}`} className="flex-shrink-0">
          {bid.horse_image ? (
            <img src={bid.horse_image} alt={bid.horse_name} className="w-16 h-16 rounded-lg object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-gray-200"></div>
          )}
        </a>
        <a href={`/asta/${bid.auction_id}`} className="flex-1 min-w-0">
          <p className="font-bold text-[#0a1628] hover:text-[#c9a227] transition-colors">{bid.horse_name || bid.title}</p>
          {!ended && <div className="mt-1"><CountdownTimer endTime={bid.end_time} /></div>}
          <StatusLabel bid={bid} ended={ended} isLeading={isLeading} />
        </a>
        <div className="text-right flex-shrink-0 mr-2">
          <p className="text-xs text-[#64748b]">La tua offerta</p>
          <p className="font-bold text-[#0a1628] text-lg">{'\u20AC'}{bid.my_highest_bid?.toLocaleString()}</p>
          <p className="text-xs text-[#64748b] mt-1">Prezzo attuale</p>
          <p className={`font-bold ${isLeading ? 'text-green-700' : 'text-red-600'}`}>
            {'\u20AC'}{bid.current_price?.toLocaleString()}
          </p>
        </div>
        {!ended && (
          <button
            onClick={() => onBid(bid)}
            className="flex-shrink-0 bg-[#0a7e8c] hover:bg-[#086a76] text-white px-5 py-3 rounded-lg font-bold text-sm transition-colors whitespace-nowrap"
            data-testid={`bid-btn-${bid.auction_id}`}
          >
            {isLeading ? 'Rilancia' : 'Fai offerta'}
          </button>
        )}
      </div>

      {/* Mobile layout */}
      <div className="md:hidden p-4">
        <a href={`/asta/${bid.auction_id}`} className="flex items-center gap-3 mb-3">
          {bid.horse_image ? (
            <img src={bid.horse_image} alt={bid.horse_name} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-gray-200 flex-shrink-0"></div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[#0a1628] text-sm">{bid.horse_name || bid.title}</p>
            <StatusLabel bid={bid} ended={ended} isLeading={isLeading} />
          </div>
        </a>
        {!ended && (
          <div className="mb-3">
            <CountdownTimer endTime={bid.end_time} />
          </div>
        )}
        <div className="flex justify-between items-end border-t border-gray-200/50 pt-3">
          <div>
            <p className="text-[10px] text-[#64748b] uppercase">La tua offerta</p>
            <p className="font-bold text-[#0a1628]">{'\u20AC'}{bid.my_highest_bid?.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#64748b] uppercase">Prezzo attuale</p>
            <p className={`font-bold ${isLeading ? 'text-green-700' : 'text-red-600'}`}>
              {'\u20AC'}{bid.current_price?.toLocaleString()}
            </p>
          </div>
        </div>
        {!ended && (
          <button
            onClick={() => onBid(bid)}
            className="w-full mt-3 bg-[#0a7e8c] hover:bg-[#086a76] text-white py-3 rounded-lg font-bold text-sm transition-colors"
            data-testid={`bid-btn-mobile-${bid.auction_id}`}
          >
            {isLeading ? 'Rilancia' : 'Fai offerta'}
          </button>
        )}
      </div>
    </div>
  );
};

const StatusLabel = ({ bid, ended, isLeading }) => {
  if (isLeading) {
    return (
      <span className="inline-flex items-center gap-1 text-green-700 text-xs font-bold mt-1" data-testid={`bid-status-leading-${bid.auction_id}`}>
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
        {ended ? 'Hai vinto!' : 'Sei il migliore offerente'}
      </span>
    );
  }
  return (
    <div className="mt-1" data-testid={`bid-status-outbid-${bid.auction_id}`}>
      <span className="inline-flex items-center gap-1 text-red-600 text-xs font-bold">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/></svg>
        {ended ? 'Non hai vinto' : 'Superato'}
      </span>
      {bid.rival_bidder_number > 0 && !ended && (
        <span className="text-red-500 text-[11px] ml-1">
          da <strong>Offerente {bid.rival_bidder_number}</strong> ({'\u20AC'}{bid.rival_amount?.toLocaleString()})
        </span>
      )}
    </div>
  );
};

export default MyBidsPage;
